import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardSidebar from '../components/DashboardSidebar';
import { Button } from '../components/ui/button';
import { 
  FileText, Clock, CheckCircle, AlertCircle, 
  ArrowRight, UserCheck, Activity
} from 'lucide-react';
import { employeeApi } from '../lib/api';
import CountryFlag from '../components/CountryFlag';

const EmployeeDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashboardRes, applicationsRes] = await Promise.all([
          employeeApi.getDashboard(),
          employeeApi.getApplications({ status: 'submitted', limit: 10 })
        ]);
        setDashboardData(dashboardRes.data);
        setApplications(applicationsRes.data.applications || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const stats = dashboardData?.stats || {};

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-500 text-white';
      case 'rejected': return 'bg-red-500 text-white';
      case 'submitted':
      case 'under_review':
      case 'processing':
        return 'bg-[#FF5A36] text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const handleAssign = async (applicationId) => {
    try {
      await employeeApi.assignApplication(applicationId);
      // Refresh applications
      const res = await employeeApi.getApplications({ status: 'submitted', limit: 10 });
      setApplications(res.data.applications || []);
    } catch (error) {
      console.error('Error assigning application:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <DashboardSidebar />
      
      <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8" data-testid="employee-dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Welcome, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-[#4A5568] mt-1">Review and process visa applications.</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 border border-[#D1CBBF]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#4A5568]">Assigned to Me</p>
                <p className="text-2xl font-bold text-[#0A1E40]">{stats.assigned_applications || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-[#D1CBBF]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#4A5568]">Pending Review</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending_review || 0}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-[#D1CBBF]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#4A5568]">Completed Today</p>
                <p className="text-2xl font-bold text-green-600">{stats.completed_today || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Link to="/employee/applications?assigned_only=true" className="block">
            <div className="bg-[#0A1E40] text-white rounded-xl p-6 card-hover">
              <UserCheck className="w-8 h-8 mb-3" />
              <h3 className="font-semibold text-lg mb-1">My Assigned Applications</h3>
              <p className="text-white/70 text-sm">View applications assigned to you</p>
            </div>
          </Link>
          <Link to="/employee/applications?status=submitted" className="block">
            <div className="bg-white border border-[#D1CBBF] rounded-xl p-6 card-hover">
              <Activity className="w-8 h-8 text-[#0A1E40] mb-3" />
              <h3 className="font-semibold text-lg text-[#0A1E40] mb-1">Review Queue</h3>
              <p className="text-[#4A5568] text-sm">Applications waiting for review</p>
            </div>
          </Link>
        </div>

        {/* Pending Applications */}
        <div className="bg-white rounded-xl border border-[#D1CBBF]">
          <div className="p-6 border-b border-[#D1CBBF] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Applications Pending Review
            </h2>
            <Link to="/employee/applications">
              <Button variant="ghost" className="text-[#FF5A36]">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
          
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Application ID</th>
                  <th>Applicant</th>
                  <th>Country</th>
                  <th>Visa Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8">
                      <div className="animate-pulse">Loading...</div>
                    </td>
                  </tr>
                ) : applications.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-[#4A5568]">
                      No pending applications
                    </td>
                  </tr>
                ) : (
                  applications.map((app) => (
                    <tr key={app.application_id} data-testid={`employee-app-row-${app.application_id}`}>
                      <td className="font-medium text-[#0A1E40]">
                        <Link to={`/employee/applications/${app.application_id}`} className="text-[#FF5A36] hover:underline">
                          {app.application_id}
                        </Link>
                      </td>
                      <td>
                        <div>
                          <p className="font-medium">{app.applicant?.name || 'Unknown'}</p>
                          <p className="text-xs text-[#4A5568]">{app.applicant?.email}</p>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <span><CountryFlag code={app.country?.code} size="sm" /></span>
                          <span>{app.country?.name}</span>
                        </div>
                      </td>
                      <td>{app.visa_type?.name}</td>
                      <td>
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(app.status)}`}>
                          {app.status?.replace('_', ' ')}
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleAssign(app.application_id)}
                            disabled={app.assigned_to}
                          >
                            {app.assigned_to ? 'Assigned' : 'Assign to Me'}
                          </Button>
                          <Link to={`/employee/applications/${app.application_id}`}>
                            <Button size="sm" className="bg-[#FF5A36] hover:bg-[#E0492B]">
                              Review
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmployeeDashboard;
